import { Copy05 } from '@untitled-ui/icons-react/build/cjs';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BrokenIcon from 'src/assets/img/icons/link-broken.svg';
import ActivityFeedCmp from 'src/components/ActivityFeedCmp';
import AvatarGroup from 'src/components/AvatarGroup';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import TabButton from 'src/components/TabButton';
import ToastBodyContainer from 'src/components/ToastContainer';
import TooltipCmp from 'src/components/TooltipCmp';
import { getBadgeLabelByOrderStatus } from 'src/constants/common';
import { ROUTES, SHARE_LINK_PREVIEW_PARAM } from 'src/constants/routes';
import { BadgeType } from 'src/interface/common';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { buildOrderLegTabs, orderDetailsData, orderDetailsLoading, selectCurrentLegData, selectCurrentLocation, setOrderData, setOrderLoading } from 'src/redux/orders.slice';
import { activitiesPaginationData, appendActivities, setActivities, setActivitiesLoading, setActivitiesPagination } from 'src/redux/Quoting.slice';
import { ERROR_STATUS, getSharedOrder, getSharedOrderActivities, LoadState, LOAD_STATE, setShareToken } from 'src/services/ShareLinkService';
import WalToast from 'src/utils/WalToast';

import OrderInfoSection from '../Orders/Common/OrderInfoSection';
import { classOptions } from '../Orders/order.const';
import OrderStatus from '../Orders/OrderStatus';
import { getOrderStatusBadgeType } from '../Orders/OrderView';
import LegOverviewSection from '../Orders/OrderView/LegOverviewSection';
import OrderDetailsWidgets from '../Orders/OrderView/OrderDetailsWidgets';
import OrderLegDetailsSection from '../Orders/OrderView/OrderLegDetailsSection';
import SharedPageTopBar from '../SharedQuote/SharedPageTopBar';

const ErrorState: React.FC<{ title: string; body: string }> = ({ title, body }) => (
    <div className="flex flex-col items-center justify-center text-center gap-5 max-w-[1480px] mx-auto rounded-xl w-full bg-white mt-4 max-lg:h-full max-lg:mb-4 lg:min-h-[500px]">
        <div className="flex flex-col items-center justify-center">
            <img src={BrokenIcon} alt="broken link" className="w-[80px] h-[80px] object-contain" />
            <div className="flex flex-col items-center gap-1.5 mt-5">
                <h1 className="text-[20px] font-semibold leading-[30px] text-grayLight900">{title}</h1>
                <p className="text-[14px] font-normal leading-[20px] text-gray500 max-lg:px-2">{body}</p>
            </div>
            <ButtonCmp className="btn-outline-primary mt-4" onClick={() => (window.location.href = ROUTES.HOME)}>
                Login to Portal
            </ButtonCmp>
        </div>
    </div>
);

const ShareOrder = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const order = useAppSelector(orderDetailsData);
    const loggedInUser = useAppSelector(currentUserSelector);
    const [searchParams] = useSearchParams();
    const token = searchParams.get('t');
    // Preview opt-out flag set by the Share Tracking Link modal — see SHARE_LINK_PREVIEW_PARAM.
    const isPreviewMode = searchParams.get(SHARE_LINK_PREVIEW_PARAM) === '1';

    const isLoading = useAppSelector(orderDetailsLoading);
    const currentLegData = useAppSelector(selectCurrentLegData);
    const currentLocation = useAppSelector(selectCurrentLocation);

    const pagination = useAppSelector(activitiesPaginationData);

    const [state, setState] = useState<LoadState>(LOAD_STATE.LOADING);
    const [errorMessage, setErrorMessage] = useState('');
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(
        () => () => {
            dispatch(setActivities([]));
            dispatch(setActivitiesPagination(null));
        },
        []
    );

    const fetchActivities = useCallback(
        async ({ lastId, append }: { lastId?: number | null; append: boolean }) => {
            dispatch(setActivitiesLoading(true));

            try {
                const response = await getSharedOrderActivities({ perPage: 25, ...(lastId ? { lastId } : {}) });
                const data = response?.data;

                if (append) {
                    dispatch(appendActivities(data?.activities || []));
                } else {
                    dispatch(setActivities(data?.activities || []));
                }

                dispatch(
                    setActivitiesPagination(
                        data?.pagination
                            ? {
                                  hasMore: data.pagination.hasMore ?? false,
                                  nextCursor: data.pagination.nextCursor ?? null,
                                  totalItems: data.pagination.totalItems ?? 0,
                              }
                            : null
                    )
                );
            } catch (err: any) {
                WalToast.error(err?.response?.data?.message || 'Failed to load activities');
            } finally {
                dispatch(setActivitiesLoading(false));
            }
        },
        [dispatch]
    );

    useEffect(() => {
        fetchActivities({ append: false });
    }, []);

    useEffect(() => {
        if (activeTab === 'activity') {
            fetchActivities({ append: false });
        }
    }, [activeTab]);

    const handleLoadMore = useCallback(async () => {
        if (!pagination?.hasMore || !pagination?.nextCursor) return;
        fetchActivities({ lastId: pagination.nextCursor, append: true });
    }, [pagination, fetchActivities]);

    const tabArray = useMemo(
        () => [
            { value: 'overview', name: t('Common.Overview') },
            { value: 'activity', name: t('Common.Activity') },
        ],
        [t]
    );

    const memorizedBadge = useMemo(() => {
        const showLoading = isLoading || !order;
        const badgeType = order && order?.orderStatus ? getOrderStatusBadgeType(order?.orderStatus as string) : 'success';
        const badgeText = !showLoading && order?.orderStatus ? getBadgeLabelByOrderStatus(order?.orderStatus as string) : t('Common.Opened');
        const mainClassName = `bg-white rounded-md ${showLoading ? 'custom-loading' : ''}`;

        return (
            <BadgeCmp style="modern" mainClassName={mainClassName} type={badgeType as BadgeType}>
                {badgeText}
            </BadgeCmp>
        );
    }, [order, isLoading, t]);

    const memorizedOrderTypeBadge = useMemo(() => {
        const showLoading = isLoading || !order;
        const orderTypeValue = order?.orderType || 'ltl';

        const badgeType: BadgeType = 'success';
        const badgeText = !showLoading && orderTypeValue ? classOptions?.find((option: any) => option?.value === orderTypeValue)?.label : t('Common.LTL');

        const mainClassName = `bg-white rounded-md ${showLoading ? 'custom-loading' : ''}`;

        return (
            <BadgeCmp style="modern" mainClassName={mainClassName} type={badgeType}>
                {badgeText}
            </BadgeCmp>
        );
    }, [order, isLoading, t]);

    const fetchOrderDetail = useCallback((cancelled: { current: boolean }) => {
        dispatch(setOrderLoading(true));

        getSharedOrder()
            .then((response: any) => {
                if (cancelled.current) return;

                const masterOrder = response?.data?.order ?? response?.data;

                // Skipped in preview mode: the Share Tracking Link modal's Preview button opens
                // this page with ?preview=1 so the (always signed-in) internal user can see the
                // recipient's view instead of being bounced to their own order detail page.
                if (!isPreviewMode && loggedInUser && masterOrder?.id) {
                    navigate(`/orders/${masterOrder.id}`, { replace: true });

                    return;
                }

                let orderLegsRaw: any[] = [];

                if (masterOrder?.orderLegs) {
                    orderLegsRaw = Array.isArray(masterOrder.orderLegs) ? masterOrder.orderLegs : JSON.parse(masterOrder.orderLegs);
                }

                const hasMultipleLegs = orderLegsRaw.length > 1;

                dispatch(
                    setOrderData({
                        // sharedDocuments: what the customer chose to share on this link (cp-meeting-changes F3);
                        // the read-only DocumentsWidget lists them instead of the sign-in teaser.
                        order: { ...masterOrder, sharedDocuments: response?.data?.documents ?? [] },
                        // Shared builder — same tab identity contract as the order detail page
                        // (master = isMaster-{order.id}, legs keyed by numeric leg id)
                        orderLegs: buildOrderLegTabs(masterOrder, orderLegsRaw),
                        currentLegData: orderLegsRaw[0] || null,
                        currentLocation: masterOrder?.orderLocationUpdateList?.[0] || masterOrder?.currentLocation || null,
                        isOrderHasLeg: hasMultipleLegs,
                    })
                );

                setState(LOAD_STATE.SUCCESS);
            })
            .catch((error: any) => {
                if (cancelled.current) return;
                const status = error?.response?.status;

                if (status === ERROR_STATUS.EXPIRED) setState(LOAD_STATE.EXPIRED);
                else if (status === ERROR_STATUS.NOT_FOUND) setState(LOAD_STATE.NOT_FOUND);
                else {
                    setErrorMessage(error?.response?.data?.message || error?.message || '');
                    setState(LOAD_STATE.ERROR);
                }
            })
            .finally(() => {
                dispatch(setOrderLoading(false));
            });
        // Deps intentionally empty, matching the rest of this file: `isPreviewMode` (and `token`)
        // come from the query string and cannot change while this page is mounted, so the first
        // render's closure is always current. Adding them would only re-create the callback and
        // re-fire the fetch effect below, which keys off `fetchOrderDetail`.
    }, []);

    useEffect(() => {
        if (!token) {
            setState(LOAD_STATE.INVALID_TOKEN);

            return;
        }

        setShareToken(token);

        const cancelled = { current: false };

        fetchOrderDetail(cancelled);

        return () => {
            cancelled.current = true;
            setShareToken(null);
        };
    }, [token, fetchOrderDetail]);

    const copyDebounce = useCallback(
        debounce((copyQuote: string) => {
            navigator.clipboard.writeText(copyQuote);
            WalToast.copy(t('Common.Copied_to_clipboard'), '', 'copy-msg');
        }, 0),
        []
    );

    return (
        <main className="h-screen flex bg-utilityBlack700 flex-col w-full">
            {/* Share pages render outside AppLayout (home of the app's only other ToastBodyContainer),
                so copy / error toasts need their own container here — same as SharedQuote. */}
            <ToastBodyContainer />
            <SharedPageTopBar />
            <div className="flex-1 flex flex-col min-h-0 lg:overflow-hidden w-full mx-auto px-2 lg:px-4 rounded-t-xl global-background-color">
                <div className="flex flex-col flex-1 min-h-0 max-w-[1480px] w-full mx-auto px-2">
                    {state === LOAD_STATE.INVALID_TOKEN ? (
                        <ErrorState title={t('ShareOrder.Invalid_link_title') as string} body={t('ShareOrder.Invalid_link_body') as string} />
                    ) : state === LOAD_STATE.EXPIRED ? (
                        <ErrorState title={t('ShareOrder.Expired_title') as string} body={t('ShareOrder.Expired_body') as string} />
                    ) : state === LOAD_STATE.NOT_FOUND ? (
                        <ErrorState title={t('ShareOrder.Not_found_title') as string} body={t('ShareOrder.Not_found_body') as string} />
                    ) : state === LOAD_STATE.ERROR ? (
                        <ErrorState title={t('ShareOrder.Error_title') as string} body={errorMessage || (t('ShareOrder.Error_body') as string)} />
                    ) : (
                        (state === LOAD_STATE.SUCCESS || state === LOAD_STATE.LOADING) && (
                            <div className="flex flex-col flex-1 min-h-0 w-full max-lg:overflow-auto scrollbar-hide max-lg:pb-[60px]">
                                {/* Fixed header area */}
                                <div className="pt-4 space-y-4 lg:pr-1 lg:sticky lg:top-0 lg:z-20 global-background-color">
                                    <div className="flex justify-between gap-3 flex-wrap border-b border-borderPrimary pb-3 !items-start lg:!items-center xls:!flex-nowrap">
                                        <div className="flex items-center md:gap-2 gap-1.5">
                                            <h6 className={`text-grayLight900 text-sm lg:text-lg font-semibold ${isLoading || !order ? 'custom-loading' : ''}`}>
                                                {isLoading || !order ? '#WAL-XXXX-1' : `#${order?.orderId}`}
                                            </h6>
                                            <TooltipCmp message={t('Common.Copy')}>
                                                <Copy05 className="h-4 w-4 lg:w-5 lg:h-5 text-primary cursor-pointer font-bold" onClick={() => copyDebounce(`#${order?.orderId}`)} />
                                            </TooltipCmp>
                                            {isLoading || !order ? (
                                                <div className="flex items-center [&>div:not(:first-child)>div>div]:border-white [&>div:not(:first-child)>div>div]:border-[1.5px] [&>div:not(:first-child)]:-ml-2">
                                                    <div className="table-profile-user-circle-sm custom-loading">SR</div>
                                                </div>
                                            ) : (
                                                order?.salesRepFirstName && (
                                                    <AvatarGroup
                                                        avatars={[
                                                            {
                                                                src: order?.salesImage ? `${order?.salesImageUrl}${order?.salesImage}` : '',
                                                                alt: `${order?.salesRepFirstName} ${order?.salesRepLastName}`,
                                                            },
                                                        ]}
                                                        maxVisible={4}
                                                    />
                                                )
                                            )}
                                            {memorizedBadge}
                                            {memorizedOrderTypeBadge}
                                        </div>
                                    </div>
                                    <OrderInfoSection />
                                </div>

                                {/* Scrollable two-column area */}
                                <div className="flex lg:overflow-auto custom-scrollbar-main flex-1 max-lg:flex-col max-lg:overflow-visible">
                                    {/* Left column */}
                                    <div className="flex-1 max-lg:flex-none flex flex-col lg:overflow-hidden lg:pr-2 pt-0 lg:pt-5 min-w-0">
                                        <div
                                            className={`flex-1 flex flex-col lg:-mx-2 lg:px-2 lg:overflow-auto custom-scrollbar-v2 lg:pb-4 pb-0 space-y-4 lg:space-y-5 ${
                                                activeTab === 'overview' && 'scroll-padding lg:!pb-[180px]'
                                            }`}
                                        >
                                            <div className="sticky top-0 z-[11] global-background-color max-lg:hidden">
                                                <TabButton
                                                    parentClassName="tab-border-bottom-parent w-full !border-b !border-borderPrimary"
                                                    tabParentClassName="lg:px-1 lg:-mx-1 global-background-color"
                                                    className="tab-border-bottom min-w-[150px] lg:min-w-[200px] !pt-[6px]"
                                                    activeClassName="tab-border-bottom-active"
                                                    tabBadgeClassName="tab-border-bottom-badge"
                                                    tabArray={tabArray}
                                                    handleOnClick={(e: any) => setActiveTab(e?.target?.dataset?.value)}
                                                    isActive={activeTab}
                                                />
                                            </div>
                                            <div className={activeTab !== 'overview' ? 'lg:hidden space-y-4 lg:space-y-5' : 'space-y-4 lg:space-y-5'}>
                                                <OrderStatus isLoading={isLoading} order={order} currentLegData={currentLegData} currentLocation={currentLocation} isReadOnly={true} />
                                                <LegOverviewSection />
                                                <OrderLegDetailsSection isReadOnly={true} />
                                            </div>
                                            <div className={activeTab !== 'activity' ? 'lg:hidden space-y-4 lg:space-y-5' : 'space-y-4 lg:space-y-5'}>
                                                <ActivityFeedCmp entityType="order" entityId={order?.id} handleLoadMore={handleLoadMore} isReadOnly={true} />
                                            </div>
                                        </div>
                                    </div>
                                    {/* Right sidebar */}
                                    <div className="widget-right-sidebar-common-wrap max-lg:!w-full flex flex-col lg:overflow-auto custom-scrollbar-v2 lg:space-y-5 space-y-4 pt-4 lg:pt-5">
                                        <OrderDetailsWidgets showCustomize={false} onOpenInRadarClick={() => (window.location.href = ROUTES.LOGIN)} />
                                    </div>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>
        </main>
    );
};

export default ShareOrder;
